import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { getSalesPersons } from '../../../../services/api/lookupApi';
import { getAnalyticsSummary } from '../../../../services/api/analyticsApi';
import MergeCustomerModal from '../customers/MergeCustomerModal';
import {
    Calendar,
    TrendingUp,
    Users,
    DollarSign,
    ArrowRight,
    XCircle,
    ShoppingBag,
    CreditCard,
    MapPin,
    Hash,
    Clock,
    User,
    BarChart3,
    Activity,
    Fuel,
    Car,
    Maximize2,
    Download,
    Search,
    ArrowUpDown,
    Eye,
    ChevronDown,
    ChevronUp,
    X,
    Merge
} from 'lucide-react';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { useAuth } from '../../../../context/AuthContext';

// Helper: convert yyyy-MM-dd to dd/MM/yyyy for UI display
const formatDateString = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
};

const OperationsAnalyticsView = ({ role }) => {
    const { user } = useAuth();
    const isSalesPerson = role === 'SALES_PERSON';

    const [dateRange, setDateRange] = useState({
        start: format(startOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        end: format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'")
    });
    const getStartDateString = () => dateRange.start ? dateRange.start.split('T')[0] : '';
    const getEndDateString = () => dateRange.end ? dateRange.end.split('T')[0] : '';
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([]);
    const [detailedData, setDetailedData] = useState({});
    const [staffList, setStaffList] = useState([]);
    const [staffFilter, setStaffFilter] = useState(isSalesPerson ? (user?.publicId || user?.id || '') : '');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isSalesPerson && user) {
            setStaffFilter(user.publicId || user.id || '');
        }
    }, [isSalesPerson, user]);

    // Modal, search and sorting states
    const [globalPendingPayments, setGlobalPendingPayments] = useState([]);
    const [expandedWidget, setExpandedWidget] = useState(null); // 'visited' | 'pending' | 'revenue' | 'leaderboard'
    const [modalSearch, setModalSearch] = useState('');
    const [revenueSortOrder, setRevenueSortOrder] = useState('desc'); // default desc: high to low revenue
    const [pendingSortOrder, setPendingSortOrder] = useState('desc');
    const [visitedSortOrder, setVisitedSortOrder] = useState('desc');
    const [leaderboardSortBy, setLeaderboardSortBy] = useState('sales');
    const [leaderboardSortOrder, setLeaderboardSortOrder] = useState('desc');
    const [selectedPendingCustomer, setSelectedPendingCustomer] = useState(null);
    const [expandedCustomerRows, setExpandedCustomerRows] = useState({});

    // Merge modal states
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [selectedMergeCustomer, setSelectedMergeCustomer] = useState(null);

    const toggleCustomerRow = useCallback((customerName) => {
        setExpandedCustomerRows(prev => ({
            ...prev,
            [customerName]: !prev[customerName]
        }));
    }, []);

    // Bulk Receive Modal state & functions
    const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentMode, setPaymentMode] = useState('CASH');
    const [referenceNo, setReferenceNo] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [modalSuccess, setModalSuccess] = useState(null);

    useEffect(() => {
        if (showBulkReceiveModal) {
            const loadCustomers = async () => {
                try {
                    const { getAllCustomers } = await import('../../../../services/api/customerApi');
                    const res = await getAllCustomers({ size: 1000, status: 'ACTIVE' });
                    setCustomerList(res.content || []);
                } catch (err) {
                    console.error("Failed to load customers for bulk payment modal:", err);
                }
            };
            loadCustomers();
        } else {
            setSelectedCustomerId('');
            setPaymentAmount('');
            setPaymentDate(new Date().toISOString().split('T')[0]);
            setPaymentMode('CASH');
            setReferenceNo('');
            setModalError(null);
            setModalSuccess(null);
        }
    }, [showBulkReceiveModal]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { getDailySalesReports, getDailySalesReport } = await import('../../../../services/api/dailySalesReportApi');

            const reportsList = await getDailySalesReports({
                startDate: getStartDateString() || null,
                endDate: getEndDateString() || null,
                salesPersonPublicId: staffFilter
            });

            // Fetch global pending payments to ignore date ranges
            const { getGlobalPendingPayments } = await import('../../../../services/api/dailySalesReportApi');
            const globalPendingData = await getGlobalPendingPayments(staffFilter);
            setGlobalPendingPayments(globalPendingData);

            setReports(reportsList);

            // Populate detailsMap directly from reportsList to avoid parallel request bottleneck
            const detailsMap = {};
            reportsList.forEach(r => {
                detailsMap[r.publicId] = r;
            });
            setDetailedData(detailsMap);
        } catch (err) {
            console.error("Failed to fetch analytics data:", err);
            setError("Failed to load operations log data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [dateRange, staffFilter]);

    const handleBulkPaymentSubmit = async (e) => {
        e.preventDefault();
        setModalLoading(true);
        setModalError(null);
        setModalSuccess(null);

        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0) {
            setModalError("Please enter a valid amount greater than zero.");
            setModalLoading(false);
            return;
        }

        if (amount > selectedCustomerOutstanding) {
            setModalError(`Payment amount cannot exceed the customer's total outstanding balance of AED ${selectedCustomerOutstanding.toFixed(2)}.`);
            setModalLoading(false);
            return;
        }

        try {
            const { recordBulkCustomerPayment } = await import('../../../../services/api/dailySalesReportApi');
            await recordBulkCustomerPayment({
                customerPublicId: selectedCustomerId,
                amount,
                paymentDate,
                paymentMode,
                referenceNo: referenceNo || null
            });

            setModalSuccess("Bulk payment received and allocated successfully!");
            // Refresh data
            fetchData();
            // Auto close after 2 seconds
            setTimeout(() => {
                setShowBulkReceiveModal(false);
            }, 2000);
        } catch (err) {
            console.error("Failed to record bulk payment:", err);
            const { handleApiError } = await import('../../../../services/api/dailySalesReportApi');
            setModalError(handleApiError(err) || "Failed to record bulk payment. Please try again.");
        } finally {
            setModalLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const list = await getSalesPersons();
                // Filter to keep only salespeople and salesmen
                setStaffList(list);
            } catch (err) {
                console.error("Failed to fetch staff:", err);
            }
        };
        fetchStaff();
    }, []);

    const handleDateChange = (e, field) => {
        const val = e.target.value;
        if (!val) return;

        const date = new Date(val);
        const iso = field === 'start'
            ? format(startOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'")
            : format(endOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");

        setDateRange(prev => ({ ...prev, [field]: iso }));
    };

    const applyQuickFilter = (type) => {
        if (type === 'all') {
            setDateRange({ start: '', end: '' });
            return;
        }
        let start, end;
        const now = new Date();
        
        switch(type) {
            case 'today':
                start = startOfDay(now);
                end = endOfDay(now);
                break;
            case 'yesterday':
                start = startOfDay(subDays(now, 1));
                end = endOfDay(subDays(now, 1));
                break;
            case 'lastweek':
                start = startOfDay(subDays(now, 7));
                end = endOfDay(now);
                break;
            default:
                return;
        }

        setDateRange({
            start: format(start, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
            end: format(end, "yyyy-MM-dd'T'HH:mm:ss'Z'")
        });
    };

    const toggleLeaderboardSort = (field) => {
        if (leaderboardSortBy === field) {
            setLeaderboardSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setLeaderboardSortBy(field);
            setLeaderboardSortOrder('desc');
        }
    };

    const renderSortHeader = (label, field, alignClass = '') => {
        const isActive = leaderboardSortBy === field;
        return (
            <th 
                onClick={() => toggleLeaderboardSort(field)}
                className={`px-6 py-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors select-none ${alignClass}`}
            >
                <div className={`flex items-center gap-1.5 ${alignClass.includes('text-right') ? 'justify-end' : (alignClass.includes('text-center') ? 'justify-center' : '')}`}>
                    <span>{label}</span>
                    <ArrowUpDown size={12} className={isActive ? 'text-blue-500' : 'text-gray-400 opacity-40'} />
                </div>
            </th>
        );
    };

    // -------------------------------------------------------------------------
    // CALCULATIONS & METRICS
    // -------------------------------------------------------------------------
    const analytics = useMemo(() => {
        let totalSales = 0;
        let totalCash = 0;
        let totalBank = 0;
        let totalB2bCash = 0;
        let totalB2bBank = 0;
        let totalCredit = 0;
        let totalExpenses = 0;
        let totalDistance = 0;
        let totalVisits = 0;

        // Keep track of stats per salesperson
        const staffStats = {};

        // Keep track of customer revenue & visits
        const customerSales = {};
        const customerVisits = {};
        const customerPending = {};
        const customerPendingDetails = {};

        // Keep track of expenses by category keywords
        const expenseCategories = {
            'Fuel / Petrol': 0,
            'Food': 0,
            'Local Purchase': 0,
            'Tolls / Parking': 0,
            'Maintenance': 0,
            'Others': 0
        };

        // Visit outcome counters
        const outcomes = {
            'Orders Placed': 0,
            'Follow-up Needed': 0,
            'Other Visits': 0
        };

        reports.forEach(r => {
            const details = detailedData[r.publicId];
            
            // Basic counts
            const distance = Math.max(0, (details?.odometerEnd || r.odoMeterEnd || 0) - (details?.odometerStart || r.odoMeterStart || 0));
            const visitsCount = details?.clientVisits?.length || r.totalVisits || 0;
            const rSales = r.totalSaleSummary || 0;
            const rExpenses = r.totalExpenses || 0;
            const rNet = r.finalNetPosition || 0;

            totalSales += rSales;
            totalExpenses += rExpenses;
            totalDistance += distance;
            totalVisits += visitsCount;

            // Group by salesperson
            const staffId = r.salesPersonPublicId;
            if (staffId) {
                if (!staffStats[staffId]) {
                    staffStats[staffId] = {
                        name: (r.salesPersonName && r.salesPersonName !== 'Unknown Staff') ? r.salesPersonName : (r.salesPersonPublicId ? `Staff (${r.salesPersonPublicId.slice(0, 8)})` : 'Staff Member'),
                        sales: 0,
                        expenses: 0,
                        netPosition: 0,
                        distance: 0,
                        visits: 0,
                        reportsCount: 0,
                        ordersCount: 0
                    };
                }
                staffStats[staffId].sales += rSales;
                staffStats[staffId].expenses += rExpenses;
                staffStats[staffId].netPosition += rNet;
                staffStats[staffId].distance += distance;
                staffStats[staffId].visits += visitsCount;
                staffStats[staffId].reportsCount += 1;
            }

            // Parse detailed records if available
            if (details) {
                // Only count non-B2B sales records (Cash + Bank + Credit, excluding B2B Cash and B2B Bank)
                const nonB2bCount = (details.salesRecords || []).filter(sr =>
                    ((Number(sr.cashAmount) || 0) > 0 || (Number(sr.bankAmount) || 0) > 0 || (Number(sr.creditAmount) || 0) > 0) &&
                    !((Number(sr.b2bCashAmount) || 0) > 0 || (Number(sr.b2bBankAmount) || 0) > 0)
                ).length;
                if (staffId) {
                    staffStats[staffId].ordersCount += nonB2bCount;
                }

                // Payment modes & customer sales
                (details.salesRecords || []).forEach(sr => {
                    totalCash += Number(sr.cashAmount) || 0;
                    totalBank += Number(sr.bankAmount) || 0;
                    totalB2bCash += Number(sr.b2bCashAmount) || 0;
                    totalB2bBank += Number(sr.b2bBankAmount) || 0;
                    totalCredit += Number(sr.creditAmount) || 0;

                    const client = sr.clientName || 'Unknown Client';
                    const srTotal = (Number(sr.cashAmount) || 0) + (Number(sr.bankAmount) || 0) + 
                                    (Number(sr.b2bCashAmount) || 0) + (Number(sr.b2bBankAmount) || 0) + 
                                    (Number(sr.creditAmount) || 0);
                    customerSales[client] = (customerSales[client] || 0) + srTotal;
                });

                // Expenses categories
                (details.expenseRecords || []).forEach(er => {
                    const desc = (er.description || '').toLowerCase();
                    const amt = Number(er.amount) || 0;

                    if (desc.includes('fuel') || desc.includes('petrol') || desc.includes('diesel') || desc.includes('gas')) {
                        expenseCategories['Fuel / Petrol'] += amt;
                    } else if (desc.includes('food') || desc.includes('meal') || desc.includes('lunch') || desc.includes('tea') || desc.includes('dinner') || desc.includes('breakfast')) {
                        expenseCategories['Food'] += amt;
                    } else if (desc.includes('local purchase') || desc.includes('local') || desc.includes('purchase')) {
                        expenseCategories['Local Purchase'] += amt;
                    } else if (desc.includes('salik') || desc.includes('toll') || desc.includes('park') || desc.includes('parking')) {
                        expenseCategories['Tolls / Parking'] += amt;
                    } else if (desc.includes('repair') || desc.includes('service') || desc.includes('wash') || desc.includes('tyre') || desc.includes('maintenance')) {
                        expenseCategories['Maintenance'] += amt;
                    } else {
                        expenseCategories['Others'] += amt;
                    }
                });

                // Outcomes & customer visits
                (details.clientVisits || []).forEach(cv => {
                    const notes = (cv.outcomeNotes || '').toLowerCase();
                    if (notes.includes('order') || notes.includes('sale') || notes.includes('sold') || notes.includes('invoice')) {
                        outcomes['Orders Placed'] += 1;
                    } else if (notes.includes('follow') || notes.includes('later') || notes.includes('next') || notes.includes('remind')) {
                        outcomes['Follow-up Needed'] += 1;
                    } else {
                        outcomes['Other Visits'] += 1;
                    }

                    const client = cv.clientName || 'Unknown Client';
                    customerVisits[client] = (customerVisits[client] || 0) + 1;
                });
            } else {
                // Fallbacks from list objects
                totalCash += r.totalCash || 0;
                totalBank += r.totalBank || 0;
                totalCredit += r.totalCredit || 0;
            }
        });


        const collectionMix = [
            { label: 'Cash', value: totalCash, color: '#10B981' },
            { label: 'Bank', value: totalBank, color: '#3B82F6' },
            { label: 'B2B Cash', value: totalB2bCash, color: '#8B5CF6' },
            { label: 'B2B Bank', value: totalB2bBank, color: '#6366F1' },
            { label: 'Credit', value: totalCredit, color: '#F59E0B' }
        ];

        const topCustomersBySales = Object.entries(customerSales)
            .map(([name, sales]) => ({ name, sales }))
            .sort((a, b) => b.sales - a.sales)
            .slice(0, 5);

        // visited but not customer (had visits, but total sales is 0 or doesn't exist)
        const visitedNotCustomer = Object.keys(customerVisits)
            .filter(name => !customerSales[name] || customerSales[name] === 0)
            .map(name => ({
                name,
                visits: customerVisits[name]
            }))
            .sort((a, b) => b.visits - a.visits);

        // Map from global API
        globalPendingPayments.forEach(p => {
            customerPending[p.customerName] = p.pendingAmount;
            customerPendingDetails[p.customerName] = p.details;
        });

        // customers with pending payments
        const pendingPaymentsList = globalPendingPayments.map(p => ({
            name: p.customerName,
            amount: p.pendingAmount
        }));
        const totalPendingAmount = pendingPaymentsList.reduce((sum, item) => sum + item.amount, 0);

        // all customers by revenue (full list)
        const customersByRevenueList = Object.entries(customerSales)
            .map(([name, sales]) => ({ name, sales }));
        const totalRevenueAmount = customersByRevenueList.reduce((sum, item) => sum + item.sales, 0);

        return {
            totalSales,
            totalCashAvailable: totalCash + totalB2bCash + totalBank,
            totalExpenses,
            totalDistance,
            totalVisits,
            collectionMix: collectionMix.filter(c => c.value > 0),
            expenseCategories: Object.entries(expenseCategories).map(([name, val]) => ({ name, val })).filter(e => e.val > 0),
            outcomes: Object.entries(outcomes).map(([name, count]) => ({ name, count })),
            leaderboard: Object.values(staffStats).sort((a, b) => b.sales - a.sales),
            topCustomersBySales,
            visitedNotCustomer,
            pendingPaymentsList,
            totalPendingAmount,
            customersByRevenueList,
            totalRevenueAmount,
            customerPendingDetails
        };
    }, [reports, detailedData, globalPendingPayments]);

    const selectedCustomerObj = useMemo(() => {
        return customerList.find(c => c.id === selectedCustomerId);
    }, [customerList, selectedCustomerId]);

    const selectedCustomerOutstanding = useMemo(() => {
        if (!selectedCustomerObj) return 0;
        const match = analytics.pendingPaymentsList.find(p => 
            p.name.toUpperCase() === selectedCustomerObj.name.toUpperCase()
        );
        return match ? match.amount : 0;
    }, [selectedCustomerObj, analytics.pendingPaymentsList]);

    // Calculate total collection for SVG chart
    const totalCollection = useMemo(() => {
        return analytics.collectionMix.reduce((sum, item) => sum + item.value, 0);
    }, [analytics]);

    // Sort leaderboard dynamically based on active filters
    const sortedLeaderboard = useMemo(() => {
        return [...analytics.leaderboard].sort((a, b) => {
            let valA, valB;
            if (leaderboardSortBy === 'sales') {
                valA = a.sales;
                valB = b.sales;
            } else if (leaderboardSortBy === 'orders') {
                valA = a.ordersCount;
                valB = b.ordersCount;
            } else if (leaderboardSortBy === 'efficiency') {
                valA = a.distance > 0 ? a.sales / a.distance : 0;
                valB = b.distance > 0 ? b.sales / b.distance : 0;
            } else if (leaderboardSortBy === 'distance') {
                valA = a.distance;
                valB = b.distance;
            } else if (leaderboardSortBy === 'visits') {
                valA = a.visits;
                valB = b.visits;
            } else if (leaderboardSortBy === 'expenses') {
                valA = a.expenses;
                valB = b.expenses;
            } else if (leaderboardSortBy === 'net') {
                valA = a.netPosition;
                valB = b.netPosition;
            } else {
                valA = a.name.toLowerCase();
                valB = b.name.toLowerCase();
                return leaderboardSortOrder === 'asc' 
                    ? valA.localeCompare(valB) 
                    : valB.localeCompare(valA);
            }
            return leaderboardSortOrder === 'asc' ? valA - valB : valB - valA;
        });
    }, [analytics.leaderboard, leaderboardSortBy, leaderboardSortOrder]);

    // Generate SVG path for a sector of the Donut Chart
    let accumulatedPercentage = 0;
    const donutSectors = useMemo(() => {
        if (totalCollection === 0) return [];
        
        return analytics.collectionMix.map(item => {
            const percentage = item.value / totalCollection;
            const startAngle = accumulatedPercentage * 360;
            accumulatedPercentage += percentage;
            const endAngle = accumulatedPercentage * 360;

            // Convert polar to cartesian coordinates
            const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
                const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
                return {
                    x: centerX + (radius * Math.cos(angleInRadians)),
                    y: centerY + (radius * Math.sin(angleInRadians))
                };
            };

            const center = 100;
            const radius = 70;
            const start = polarToCartesian(center, center, radius, endAngle);
            const end = polarToCartesian(center, center, radius, startAngle);
            const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

            const d = [
                "M", start.x, start.y, 
                "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
            ].join(" ");

            return {
                ...item,
                d,
                percentage
            };
        });
    }, [analytics, totalCollection]);

    // Financial Cards Data
    const financialSummary = useMemo(() => {
        return [
            { label: 'Gross Operations Revenue', value: analytics.totalSales, bg: 'bg-gradient-to-tr from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-indigo-950/20', text: 'text-blue-900 dark:text-blue-200', icon: DollarSign },
            { label: 'Total Cash Collected', value: analytics.totalCashAvailable, bg: 'bg-gradient-to-tr from-emerald-50 to-teal-50 dark:from-slate-900 dark:to-emerald-950/20', text: 'text-emerald-900 dark:text-emerald-200', icon: TrendingUp },
            { label: 'Total Operating Expenses', value: analytics.totalExpenses, bg: 'bg-gradient-to-tr from-rose-50 to-orange-50 dark:from-slate-900 dark:to-rose-950/20', text: 'text-rose-900 dark:text-rose-200', icon: CreditCard },
            { label: 'Distance Covered (KM)', value: analytics.totalDistance, bg: 'bg-gradient-to-tr from-slate-50 to-gray-100 dark:from-slate-900 dark:to-slate-800', text: 'text-slate-800 dark:text-slate-200', icon: Car, noCurrency: true }
        ];
    }, [analytics]);

    return (
        <div className="flex-1 flex flex-col h-full bg-[var(--color-bg-primary)] overflow-hidden">
            {/* Header Filter Panel */}
            <div className="p-4 md:p-6 border-b border-[var(--color-border)] flex flex-col lg:flex-row lg:items-center justify-between gap-4 shrink-0">
                <div>
                    <h1 className="text-2xl font-black text-[var(--color-text-primary)] flex items-center gap-2">
                        <BarChart3 className="text-blue-600" />
                        Operations Analytics
                    </h1>
                    <p className="text-[var(--color-text-muted)] text-sm mt-1">
                        Track payments mix, route efficiency, and team performance
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <CustomDatePicker
                        mode="range"
                        startDate={getStartDateString()}
                        endDate={getEndDateString()}
                        onStartChange={(val) => handleDateChange({ target: { value: val } }, 'start')}
                        onEndChange={(val) => handleDateChange({ target: { value: val } }, 'end')}
                    />
                    
                    <div className="flex items-center gap-1 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl p-1 shadow-sm">
                        {['today', 'yesterday', 'lastweek', 'all'].map((type) => (
                            <button
                                key={type}
                                onClick={() => applyQuickFilter(type)}
                                className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                                    (type === 'today' && dateRange.start && format(new Date(dateRange.start), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) ||
                                    (type === 'yesterday' && dateRange.start && format(new Date(dateRange.start), 'yyyy-MM-dd') === format(subDays(new Date(), 1), 'yyyy-MM-dd')) ||
                                    (type === 'lastweek' && dateRange.start && format(new Date(dateRange.start), 'yyyy-MM-dd') === format(subDays(new Date(), 7), 'yyyy-MM-dd')) ||
                                    (type === 'all' && (!dateRange.start && !dateRange.end))
                                    ? 'bg-blue-600 text-white shadow-md' 
                                    : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                                }`}
                            >
                                {type === 'lastweek' ? '7 Days' : type === 'all' ? 'All' : type}
                            </button>
                        ))}
                    </div>

                    {!isSalesPerson && (
                        <select
                            value={staffFilter}
                            onChange={(e) => setStaffFilter(e.target.value)}
                            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest shadow-sm focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value="">All Staff</option>
                            {staffList.map(s => (
                                <option key={s.publicId} value={s.publicId}>{s.name}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {/* Scrollable Dashboard Panel */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
                {loading ? (
                    <div className="h-64 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
                        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                        <p className="font-bold">Aggregating Log Metrics...</p>
                    </div>
                ) : error ? (
                    <div className="h-64 flex flex-col items-center justify-center text-rose-500 font-bold p-6 border border-dashed border-rose-500/20 rounded-2xl">
                        <XCircle size={48} className="mb-4" />
                        <p>{error}</p>
                        <button onClick={fetchData} className="mt-4 text-blue-500 hover:underline">Try Refreshing</button>
                    </div>
                ) : reports.length === 0 ? (
                    <div className="h-64 flex flex-col items-center justify-center text-gray-400 py-12">
                        <Activity size={48} className="mb-4 text-gray-300" />
                        <p className="font-bold text-lg">No Operations Data Found</p>
                        <p className="text-sm mt-1">Try expanding the date range or choosing a different staff member.</p>
                    </div>
                ) : (
                    <div className="max-w-7xl mx-auto space-y-6">
                        {/* Financial Summary Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            {financialSummary.map((card, idx) => {
                                const Icon = card.icon;
                                const valStr = card.noCurrency ? String(card.value) : `AED ${Number(card.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                                const len = valStr.length;
                                let textSizeClass = 'text-base min-[380px]:text-lg sm:text-2xl';
                                if (len > 15) {
                                    textSizeClass = 'text-[11px] min-[380px]:text-xs sm:text-lg';
                                } else if (len > 12) {
                                    textSizeClass = 'text-xs min-[380px]:text-sm sm:text-xl';
                                } else if (len > 9) {
                                    textSizeClass = 'text-sm min-[380px]:text-base sm:text-2xl';
                                }

                                return (
                                    <div key={idx} className={`${card.bg} p-4 sm:p-6 rounded-2xl border border-[var(--color-border)] shadow-xs flex items-center justify-between gap-3 min-w-0`}>
                                        <div className="space-y-1 min-w-0 flex-1">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-[var(--color-text-muted)] truncate" title={card.label}>{card.label}</p>
                                            <p className={`${textSizeClass} font-black ${card.text} tracking-tight break-words`} title={valStr}>
                                                {valStr}
                                            </p>
                                        </div>
                                        <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-white/50 dark:bg-slate-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-inner shrink-0">
                                            <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Middle Graphs Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Donut Chart Widget */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col">
                                <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider mb-6 flex items-center gap-1.5">
                                    <ShoppingBag size={14} />
                                    Payment Mix Breakdown
                                </h3>
                                
                                {totalCollection === 0 ? (
                                    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">No transaction payment data</div>
                                ) : (
                                    <div className="flex-1 flex flex-col md:flex-row items-center justify-center gap-6">
                                        <div className="relative w-40 h-40">
                                            <svg width="100%" height="100%" viewBox="0 0 200 200" className="transform -rotate-90">
                                                {donutSectors.map((sec, i) => (
                                                    <path
                                                        key={i}
                                                        d={sec.d}
                                                        fill="none"
                                                        stroke={sec.color}
                                                        strokeWidth="20"
                                                        className="transition-all hover:stroke-[24px] cursor-pointer"
                                                        style={{ transformOrigin: 'center' }}
                                                    />
                                                ))}
                                            </svg>
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Total</span>
                                                <span className="text-lg font-black text-[var(--color-text-primary)]">
                                                    AED {analytics.totalSales >= 1000 ? `${(analytics.totalSales/1000).toFixed(1)}k` : analytics.totalSales.toFixed(0)}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-col gap-2.5 flex-1 w-full">
                                            {donutSectors.map((sec, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs border-b border-gray-50 dark:border-slate-800 pb-1">
                                                    <div className="flex items-center gap-2 font-bold text-[var(--color-text-primary)]">
                                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sec.color }}></span>
                                                        <span>{sec.label}</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="font-black text-[var(--color-text-primary)]">{(sec.percentage * 100).toFixed(1)}%</span>
                                                        <p className="text-[10px] text-gray-400 font-bold">AED {sec.value.toFixed(0)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Odometer & Mileage Efficiency Card */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col">
                                <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider mb-6 flex items-center gap-1.5">
                                    <MapPin size={14} />
                                    Route & Logistics Efficiency
                                </h3>

                                <div className="space-y-4 flex-1 flex flex-col justify-center">
                                    {/* Efficiency Stat */}
                                    <div className="flex items-center justify-between p-4 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)]">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-[var(--color-text-secondary)] uppercase tracking-widest">Average Revenue / KM</span>
                                            <p className="text-xl font-black text-[var(--color-text-primary)]">
                                                AED {analytics.totalDistance > 0 ? (analytics.totalSales / analytics.totalDistance).toFixed(2) : '0.00'}
                                            </p>
                                        </div>
                                        <div className="p-2.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg shrink-0">
                                            <Fuel size={18} />
                                        </div>
                                    </div>

                                    {/* Visits Stat */}
                                    <div className="flex items-center justify-between p-4 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)]">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-[var(--color-text-secondary)] uppercase tracking-widest">Total Client Visits</span>
                                            <p className="text-xl font-black text-[var(--color-text-primary)]">
                                                {analytics.totalVisits} <span className="text-xs font-bold text-[var(--color-text-secondary)]">visits</span>
                                            </p>
                                        </div>
                                        <div className="p-2.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg shrink-0">
                                            <Users size={18} />
                                        </div>
                                    </div>

                                    {/* Average Visits / Route */}
                                    <div className="flex items-center justify-between p-4 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)]">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-[var(--color-text-secondary)] uppercase tracking-widest">Average Visits / Day</span>
                                            <p className="text-xl font-black text-[var(--color-text-primary)]">
                                                {reports.length > 0 ? (analytics.totalVisits / reports.length).toFixed(1) : '0'}
                                            </p>
                                        </div>
                                        <div className="p-2.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-lg shrink-0">
                                            <Clock size={18} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Expenses Categories card */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col">
                                <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider mb-6 flex items-center gap-1.5">
                                    <CreditCard size={14} />
                                    Expense Categories
                                </h3>

                                {analytics.expenseCategories.length === 0 ? (
                                    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">No operational expenses recorded</div>
                                ) : (
                                    <div className="space-y-4 flex-1 flex flex-col justify-center">
                                        {analytics.expenseCategories.map((exp, i) => {
                                            const pct = analytics.totalExpenses > 0 ? (exp.val / analytics.totalExpenses) * 100 : 0;
                                            return (
                                                <div key={i} className="space-y-1.5">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="font-bold text-[var(--color-text-primary)]">{exp.name}</span>
                                                        <span className="font-black text-rose-500">AED {exp.val.toFixed(0)} ({pct.toFixed(0)}%)</span>
                                                    </div>
                                                    <div className="h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-rose-500 rounded-full transition-all duration-500" 
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Team Leaderboard Table */}
                        {!isSalesPerson && (
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                                        <Users size={14} />
                                        Salesperson Performance Leaderboard
                                    </h3>
                                    <div className="flex items-center gap-1.5">
                                        <button 
                                            onClick={async () => {
                                                try {
                                                    const { downloadLeaderboardPdf } = await import('../../../../services/api/dailySalesReportApi');
                                                    await downloadLeaderboardPdf(getStartDateString() || null, getEndDateString() || null, staffFilter, leaderboardSortBy, leaderboardSortOrder);
                                                } catch (err) {
                                                    console.error("Failed to download leaderboard PDF", err);
                                                }
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Export to PDF"
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setModalSearch('');
                                                setExpandedWidget('leaderboard');
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Expand View"
                                        >
                                            <Maximize2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-900 border-b border-[var(--color-border)] text-slate-400 dark:text-slate-500 uppercase tracking-widest font-black text-[9px]">
                                                {renderSortHeader("Salesperson", "name")}
                                                {renderSortHeader("Daily Logs", "reportsCount", "text-center")}
                                                {renderSortHeader("Distance (KM)", "distance", "text-right")}
                                                {renderSortHeader("Visits", "visits", "text-right")}
                                                {renderSortHeader("Sales", "orders", "text-right")}
                                                {renderSortHeader("Revenue (AED)", "sales", "text-right")}
                                                {renderSortHeader("Expenses (AED)", "expenses", "text-right")}
                                                {renderSortHeader("Sales Efficiency", "efficiency", "text-right")}
                                                {renderSortHeader("Net Position", "net", "text-right")}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text-primary)]">
                                            {sortedLeaderboard.map((staff, index) => {
                                                const avgRevPerKm = staff.distance > 0 ? staff.sales / staff.distance : 0;
                                                return (
                                                    <tr key={index} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors font-medium">
                                                        <td className="px-6 py-4 flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 dark:bg-slate-800 dark:text-blue-400 flex items-center justify-center font-black">
                                                                {index + 1}
                                                            </div>
                                                            <span className="font-bold">{staff.name}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center font-bold text-slate-400">{staff.reportsCount}</td>
                                                        <td className="px-6 py-4 text-right font-bold">{staff.distance} KM</td>
                                                        <td className="px-6 py-4 text-right font-bold">{staff.visits}</td>
                                                        <td className="px-6 py-4 text-right font-bold">{staff.ordersCount}</td>
                                                        <td className="px-6 py-4 text-right font-black text-emerald-600 dark:text-emerald-400">
                                                            AED {staff.sales.toFixed(2)}
                                                        </td>
                                                        <td className="px-6 py-4 text-right text-rose-500 font-bold">AED {staff.expenses.toFixed(2)}</td>
                                                        <td className="px-6 py-4 text-right font-black text-blue-600 dark:text-blue-400">
                                                            AED {avgRevPerKm.toFixed(2)} / KM
                                                        </td>
                                                        <td className={`px-6 py-4 text-right font-black ${staff.netPosition >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                            AED {staff.netPosition.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {sortedLeaderboard.length > 0 && (() => {
                                                const totals = sortedLeaderboard.reduce((acc, staff) => {
                                                    acc.reportsCount += staff.reportsCount || 0;
                                                    acc.distance += staff.distance || 0;
                                                    acc.visits += staff.visits || 0;
                                                    acc.ordersCount += staff.ordersCount || 0;
                                                    acc.sales += staff.sales || 0;
                                                    acc.expenses += staff.expenses || 0;
                                                    acc.netPosition += staff.netPosition || 0;
                                                    return acc;
                                                }, { reportsCount: 0, distance: 0, visits: 0, ordersCount: 0, sales: 0, expenses: 0, netPosition: 0 });
                                                
                                                const totalAvgRevPerKm = totals.distance > 0 ? totals.sales / totals.distance : 0;
                                                
                                                return (
                                                    <tr className="bg-slate-100/50 dark:bg-slate-900/60 font-black border-t-2 border-[var(--color-border)] text-gray-900 dark:text-gray-100">
                                                        <td className="px-6 py-4 font-black">TOTAL</td>
                                                        <td className="px-6 py-4 text-center">{totals.reportsCount}</td>
                                                        <td className="px-6 py-4 text-right">{totals.distance} KM</td>
                                                        <td className="px-6 py-4 text-right">{totals.visits}</td>
                                                        <td className="px-6 py-4 text-right">{totals.ordersCount}</td>
                                                        <td className="px-6 py-4 text-right text-emerald-600 dark:text-emerald-400">AED {totals.sales.toFixed(2)}</td>
                                                        <td className="px-6 py-4 text-right text-rose-500 font-bold">AED {totals.expenses.toFixed(2)}</td>
                                                        <td className="px-6 py-4 text-right text-blue-600 dark:text-blue-400">AED {totalAvgRevPerKm.toFixed(2)} / KM</td>
                                                        <td className={`px-6 py-4 text-right ${totals.netPosition >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>AED {totals.netPosition.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Customer Insights Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Clients Visited but Not Customers */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col h-[320px] relative group">
                                <div className="mb-4 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                                            <Users size={14} />
                                            Visited but Not Customers
                                        </h3>
                                        <p className="text-sm font-black text-[var(--color-text-primary)] mt-1">
                                            Total: {analytics.visitedNotCustomer.length} Prospects
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <button 
                                            onClick={() => setVisitedSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Toggle Sort Order"
                                        >
                                            <ArrowUpDown size={14} />
                                        </button>
                                        <button 
                                            onClick={async () => {
                                                try {
                                                    const { downloadVisitedNotConvertedPdf } = await import('../../../../services/api/dailySalesReportApi');
                                                    await downloadVisitedNotConvertedPdf(getStartDateString() || null, getEndDateString() || null, staffFilter, visitedSortOrder);
                                                } catch (err) {
                                                    console.error("Failed to download visited prospects PDF", err);
                                                }
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Export to PDF"
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setModalSearch('');
                                                setExpandedWidget('visited');
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Expand View"
                                        >
                                            <Maximize2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-1">
                                    {analytics.visitedNotCustomer.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-gray-400 text-xs py-8">No matching prospects found</div>
                                    ) : (
                                        [...analytics.visitedNotCustomer]
                                            .sort((a, b) => visitedSortOrder === 'asc' ? a.visits - b.visits : b.visits - a.visits)
                                            .map((cust, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs p-2.5 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)]">
                                                    <span className="font-bold text-[var(--color-text-primary)]">{cust.name}</span>
                                                    <span className="font-black text-blue-600 dark:text-blue-400">{cust.visits} {cust.visits === 1 ? 'visit' : 'visits'}</span>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>

                            {/* Pending Payments by Customer */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col h-[320px] relative group">
                                <div className="mb-4 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                                            <CreditCard size={14} />
                                            Pending Payments
                                        </h3>
                                        <p className="text-sm font-black text-rose-500 mt-1">
                                            Total: AED {analytics.totalPendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => setShowBulkReceiveModal(true)}
                                            className="px-2 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm shrink-0 flex items-center gap-1"
                                            title="Bulk Receive B2B Payment"
                                        >
                                            <CreditCard size={10} />
                                            <span>Bulk Receive</span>
                                        </button>
                                        <button 
                                            onClick={() => setPendingSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Toggle Sort Order"
                                        >
                                            <ArrowUpDown size={14} />
                                        </button>
                                        <button 
                                            onClick={async () => {
                                                try {
                                                    const { downloadPendingPaymentsPdf } = await import('../../../../services/api/dailySalesReportApi');
                                                    await downloadPendingPaymentsPdf(getStartDateString() || null, getEndDateString() || null, staffFilter, pendingSortOrder);
                                                } catch (err) {
                                                    console.error("Failed to download pending payments PDF", err);
                                                }
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Export to PDF"
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setModalSearch('');
                                                setExpandedWidget('pending');
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Expand View"
                                        >
                                            <Maximize2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-1">
                                    {analytics.pendingPaymentsList.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-gray-400 text-xs py-8">No pending payments recorded</div>
                                    ) : (
                                        [...analytics.pendingPaymentsList]
                                            .sort((a, b) => pendingSortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount)
                                            .map((cust, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs p-2.5 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)] group/row">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="font-bold text-[var(--color-text-primary)] truncate">{cust.name}</span>
                                                        <button 
                                                            onClick={() => setSelectedPendingCustomer({
                                                                name: cust.name,
                                                                amount: cust.amount,
                                                                details: analytics.customerPendingDetails[cust.name] || []
                                                            })}
                                                            className="p-1 rounded-md bg-[var(--color-bg-card)] hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 opacity-100 sm:opacity-0 sm:group-hover/row:opacity-100 transition-opacity flex-shrink-0"
                                                            title="View Invoices Breakdown"
                                                        >
                                                            <Eye size={12} />
                                                        </button>
                                                    </div>
                                                    <span className="font-black text-rose-500 flex-shrink-0">AED {cust.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>

                            {/* Customers by Revenue */}
                            <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm flex flex-col h-[320px] relative group">
                                <div className="mb-4 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xs font-black text-[var(--color-text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                                            <DollarSign size={14} />
                                            Customers by Revenue
                                        </h3>
                                        <p className="text-sm font-black text-emerald-600 dark:text-emerald-400 mt-1">
                                            Total: AED {analytics.totalRevenueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <button 
                                            onClick={() => setRevenueSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Toggle Sort Order"
                                        >
                                            <ArrowUpDown size={14} />
                                        </button>
                                        <button 
                                            onClick={async () => {
                                                try {
                                                    const { downloadCustomersByRevenuePdf } = await import('../../../../services/api/dailySalesReportApi');
                                                    await downloadCustomersByRevenuePdf(getStartDateString() || null, getEndDateString() || null, staffFilter, revenueSortOrder);
                                                } catch (err) {
                                                    console.error("Failed to download customer revenue PDF", err);
                                                }
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Export to PDF"
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setModalSearch('');
                                                setExpandedWidget('revenue');
                                            }}
                                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                            title="Expand View"
                                        >
                                            <Maximize2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-1">
                                    {analytics.customersByRevenueList.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-gray-400 text-xs py-8">No customer revenue recorded</div>
                                    ) : (
                                        [...analytics.customersByRevenueList]
                                            .sort((a, b) => revenueSortOrder === 'asc' ? a.sales - b.sales : b.sales - a.sales)
                                            .map((cust, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs p-2.5 bg-[var(--color-bg-hover)] rounded-xl border border-[var(--color-border)]">
                                                    <span className="font-bold text-[var(--color-text-primary)]">{cust.name}</span>
                                                    <span className="font-black text-emerald-600 dark:text-emerald-400">
                                                        AED {cust.sales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Expand Widget Popup Modal */}
            {expandedWidget && (() => {
                let title = '';
                let data = [];
                let isCurrency = false;
                let isCount = false;
                let currentSort = 'desc';
                let setSort = () => {};
                let columns = [];
                let summaryLabel = '';
                let summaryValue = '';

                if (expandedWidget === 'visited') {
                    title = 'Visited but Not Customers';
                    data = analytics.visitedNotCustomer;
                    isCount = true;
                    currentSort = visitedSortOrder;
                    setSort = setVisitedSortOrder;
                    columns = ['Prospect Client Name', 'Total Visits'];
                    summaryLabel = 'Total Prospects';
                    summaryValue = `${data.length}`;
                } else if (expandedWidget === 'pending') {
                    title = 'Pending Payments by Customer';
                    data = analytics.pendingPaymentsList;
                    isCurrency = true;
                    currentSort = pendingSortOrder;
                    setSort = setPendingSortOrder;
                    columns = ['Customer Name', 'Outstanding Amount'];
                    summaryLabel = 'Total Outstanding';
                    summaryValue = `AED ${analytics.totalPendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                } else if (expandedWidget === 'revenue') {
                    title = 'Customers by Revenue';
                    data = analytics.customersByRevenueList;
                    isCurrency = true;
                    currentSort = revenueSortOrder;
                    setSort = setRevenueSortOrder;
                    columns = ['Customer Name', 'Total Sales Revenue'];
                    summaryLabel = 'Total Sales';
                    summaryValue = `AED ${analytics.totalRevenueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                } else if (expandedWidget === 'leaderboard') {
                    title = 'Salesperson Performance Leaderboard';
                    data = analytics.leaderboard;
                    currentSort = leaderboardSortOrder;
                    setSort = setLeaderboardSortOrder;
                    summaryLabel = 'Total Salespeople';
                    summaryValue = `${data.length}`;
                }

                // Filter by search
                const filteredData = data.filter(item => 
                    (item.name || '').toLowerCase().includes(modalSearch.toLowerCase())
                );

                // Sort
                const sortedData = [...filteredData].sort((a, b) => {
                    if (expandedWidget === 'leaderboard') {
                        let valA, valB;
                        if (leaderboardSortBy === 'sales') {
                            valA = a.sales;
                            valB = b.sales;
                        } else if (leaderboardSortBy === 'orders') {
                            valA = a.ordersCount;
                            valB = b.ordersCount;
                        } else if (leaderboardSortBy === 'efficiency') {
                            valA = a.distance > 0 ? a.sales / a.distance : 0;
                            valB = b.distance > 0 ? b.sales / b.distance : 0;
                        } else if (leaderboardSortBy === 'distance') {
                            valA = a.distance;
                            valB = b.distance;
                        } else if (leaderboardSortBy === 'visits') {
                            valA = a.visits;
                            valB = b.visits;
                        } else if (leaderboardSortBy === 'expenses') {
                            valA = a.expenses;
                            valB = b.expenses;
                        } else if (leaderboardSortBy === 'net') {
                            valA = a.netPosition;
                            valB = b.netPosition;
                        } else {
                            valA = a.name.toLowerCase();
                            valB = b.name.toLowerCase();
                            return leaderboardSortOrder === 'asc' 
                                ? valA.localeCompare(valB) 
                                : valB.localeCompare(valA);
                        }
                        return leaderboardSortOrder === 'asc' ? valA - valB : valB - valA;
                    } else {
                        const valA = isCount ? a.visits : (isCurrency ? a.amount || a.sales : 0);
                        const valB = isCount ? b.visits : (isCurrency ? b.amount || b.sales : 0);
                        return currentSort === 'asc' ? valA - valB : valB - valA;
                    }
                });

                return (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl shadow-2xl w-full max-w-5xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden">
                            {/* Modal Header */}
                            <div className="p-5 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
                                <div>
                                    <h3 className="text-base font-black text-[var(--color-text-primary)]">{title}</h3>
                                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                        {(() => {
                                            const spName = staffFilter
                                                ? (staffList.find(s => s.publicId === staffFilter)?.name
                                                    || (isSalesPerson && user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : null))
                                                : null;
                                            return spName
                                                ? <>Showing full detailed data list &mdash; <span className="font-bold text-[var(--color-text-primary)]">{spName}</span></>
                                                : 'Showing full detailed data list';
                                        })()}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => {
                                        setExpandedWidget(null);
                                        setExpandedCustomerRows({});
                                    }}
                                    className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Filters & Meta */}
                            <div className="p-4 bg-[var(--color-bg-hover)] border-b border-[var(--color-border)] flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <input 
                                        type="text" 
                                        value={modalSearch}
                                        onChange={(e) => setModalSearch(e.target.value)}
                                        placeholder="Search by salesperson or client name..."
                                        className="w-full bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl pl-10 pr-4 py-2 text-xs font-bold text-[var(--color-text-primary)] focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    {expandedWidget !== 'leaderboard' && (
                                        <button 
                                            onClick={() => setSort(prev => prev === 'asc' ? 'desc' : 'asc')}
                                            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1.5 text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
                                        >
                                            <ArrowUpDown size={14} />
                                            Sort: {currentSort.toUpperCase()}
                                        </button>
                                    )}
                                    <button 
                                        onClick={async () => {
                                            try {
                                                const { 
                                                    downloadVisitedNotConvertedPdf, 
                                                    downloadPendingPaymentsPdf, 
                                                    downloadCustomersByRevenuePdf,
                                                    downloadLeaderboardPdf
                                                } = await import('../../../../services/api/dailySalesReportApi');
                                                const startStr = getStartDateString() || null;
                                                const endStr = getEndDateString() || null;
                                                if (expandedWidget === 'visited') {
                                                    await downloadVisitedNotConvertedPdf(startStr, endStr, staffFilter, currentSort);
                                                } else if (expandedWidget === 'pending') {
                                                    await downloadPendingPaymentsPdf(startStr, endStr, staffFilter, currentSort, true);
                                                } else if (expandedWidget === 'revenue') {
                                                    await downloadCustomersByRevenuePdf(startStr, endStr, staffFilter, currentSort);
                                                } else if (expandedWidget === 'leaderboard') {
                                                    await downloadLeaderboardPdf(startStr, endStr, staffFilter, leaderboardSortBy, leaderboardSortOrder);
                                                }
                                            } catch (err) {
                                                console.error("Failed to download PDF in modal", err);
                                            }
                                        }}
                                        className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1.5 text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
                                    >
                                        <Download size={14} />
                                        Export
                                    </button>
                                </div>
                            </div>

                            {/* Summary Bar */}
                            <div className="px-5 py-3.5 bg-blue-500/5 border-b border-[var(--color-border)] flex items-center justify-between text-xs shrink-0">
                                <span className="font-bold text-[var(--color-text-secondary)]">{summaryLabel}:</span>
                                <span className={`font-black ${expandedWidget === 'pending' ? 'text-rose-500' : (expandedWidget === 'revenue' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600')}`}>
                                    {summaryValue}
                                </span>
                            </div>

                            {/* Scrollable Data Table */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar p-5">
                                {sortedData.length === 0 ? (
                                    <div className="h-32 flex items-center justify-center text-gray-400 text-xs">No records found matching search query</div>
                                ) : expandedWidget === 'leaderboard' ? (
                                    <div className="border border-[var(--color-border)] rounded-xl overflow-hidden overflow-x-auto">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-[var(--color-border)] text-[var(--color-text-secondary)] uppercase tracking-widest font-black text-[9px]">
                                                    {renderSortHeader("Salesperson", "name")}
                                                    {renderSortHeader("Daily Logs", "reportsCount", "text-center")}
                                                    {renderSortHeader("Distance (KM)", "distance", "text-right")}
                                                    {renderSortHeader("Visits", "visits", "text-right")}
                                                    {renderSortHeader("Sales", "orders", "text-right")}
                                                    {renderSortHeader("Revenue (AED)", "sales", "text-right")}
                                                    {renderSortHeader("Expenses (AED)", "expenses", "text-right")}
                                                    {renderSortHeader("Sales Efficiency", "efficiency", "text-right")}
                                                    {renderSortHeader("Net Position", "net", "text-right")}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text-primary)] font-medium">
                                                {sortedData.map((staff, idx) => {
                                                    const avgRevPerKm = staff.distance > 0 ? staff.sales / staff.distance : 0;
                                                    return (
                                                        <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                            <td className="px-4 py-3.5 font-bold flex items-center gap-2">
                                                                <span className="text-gray-400 font-black">{idx + 1}.</span>
                                                                <span>{staff.name}</span>
                                                            </td>
                                                            <td className="px-4 py-3.5 text-center font-bold text-slate-400">{staff.reportsCount}</td>
                                                            <td className="px-4 py-3.5 text-right">{staff.distance} KM</td>
                                                            <td className="px-4 py-3.5 text-right">{staff.visits}</td>
                                                            <td className="px-4 py-3.5 text-right">{staff.ordersCount}</td>
                                                            <td className="px-4 py-3.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                                                                AED {staff.sales.toFixed(2)}
                                                            </td>
                                                            <td className="px-4 py-3.5 text-right text-rose-500 font-bold">AED {staff.expenses.toFixed(2)}</td>
                                                            <td className="px-4 py-3.5 text-right font-black text-blue-600 dark:text-blue-400">
                                                                AED {avgRevPerKm.toFixed(2)} / KM
                                                            </td>
                                                            <td className={`px-4 py-3.5 text-right font-black ${staff.netPosition >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                                AED {staff.netPosition.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {sortedData.length > 0 && (() => {
                                                    const totals = sortedData.reduce((acc, staff) => {
                                                        acc.reportsCount += staff.reportsCount || 0;
                                                        acc.distance += staff.distance || 0;
                                                        acc.visits += staff.visits || 0;
                                                        acc.ordersCount += staff.ordersCount || 0;
                                                        acc.sales += staff.sales || 0;
                                                        acc.expenses += staff.expenses || 0;
                                                        acc.netPosition += staff.netPosition || 0;
                                                        return acc;
                                                    }, { reportsCount: 0, distance: 0, visits: 0, ordersCount: 0, sales: 0, expenses: 0, netPosition: 0 });
                                                    
                                                    const totalAvgRevPerKm = totals.distance > 0 ? totals.sales / totals.distance : 0;
                                                    
                                                    return (
                                                        <tr className="bg-slate-100/50 dark:bg-slate-900/60 font-black border-t-2 border-[var(--color-border)] text-gray-900 dark:text-gray-100">
                                                            <td className="px-4 py-3.5 font-black">TOTAL</td>
                                                            <td className="px-4 py-3.5 text-center">{totals.reportsCount}</td>
                                                            <td className="px-4 py-3.5 text-right">{totals.distance} KM</td>
                                                            <td className="px-4 py-3.5 text-right">{totals.visits}</td>
                                                            <td className="px-4 py-3.5 text-right">{totals.ordersCount}</td>
                                                            <td className="px-4 py-3.5 text-right text-emerald-600 dark:text-emerald-400">AED {totals.sales.toFixed(2)}</td>
                                                            <td className="px-4 py-3.5 text-right text-rose-500 font-bold">AED {totals.expenses.toFixed(2)}</td>
                                                            <td className="px-4 py-3.5 text-right text-blue-600 dark:text-blue-400">AED {totalAvgRevPerKm.toFixed(2)} / KM</td>
                                                            <td className={`px-4 py-3.5 text-right ${totals.netPosition >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>AED {totals.netPosition.toFixed(2)}</td>
                                                        </tr>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="border border-[var(--color-border)] rounded-xl overflow-hidden overflow-x-auto">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-[var(--color-border)] text-[var(--color-text-secondary)] uppercase tracking-widest font-black text-[9px]">
                                                    <th className="px-4 py-3">{columns[0]}</th>
                                                    <th className="px-4 py-3 text-right">{columns[1]}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text-primary)]">
                                                {sortedData.map((item, idx) => {
                                                    const isExpanded = !!expandedCustomerRows[item.name];
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                                <td className="px-4 py-3.5 font-bold flex items-center gap-2">
                                                                    {expandedWidget === 'pending' && (
                                                                        <button 
                                                                            onClick={() => toggleCustomerRow(item.name)}
                                                                            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 transition-colors flex-shrink-0"
                                                                            title={isExpanded ? "Collapse Invoices" : "Expand Invoices"}
                                                                        >
                                                                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                                        </button>
                                                                    )}
                                                                    <span 
                                                                        className={expandedWidget === 'pending' ? 'cursor-pointer hover:text-blue-500 transition-colors' : ''}
                                                                        onClick={() => expandedWidget === 'pending' && toggleCustomerRow(item.name)}
                                                                    >
                                                                        {item.name}
                                                                    </span>
                                                                    {!isSalesPerson && (expandedWidget === 'pending' || expandedWidget === 'revenue') && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedMergeCustomer({ id: item.publicId || item.id, name: item.name });
                                                                                setShowMergeModal(true);
                                                                            }}
                                                                            className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-md transition-colors flex items-center gap-1 inline-flex"
                                                                            title="Merge duplicate customer records"
                                                                        >
                                                                            <Merge size={11} />
                                                                            Merge
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td className={`px-4 py-3.5 text-right font-black ${isCount ? 'text-blue-600 dark:text-blue-400' : (expandedWidget === 'pending' ? 'text-rose-500' : 'text-emerald-600 dark:text-emerald-400')}`}>
                                                                    {isCount ? `${item.visits} visits` : `AED ${(item.amount || item.sales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                                </td>
                                                            </tr>
                                                            {expandedWidget === 'pending' && isExpanded && (
                                                                <tr className="bg-slate-50/30 dark:bg-slate-900/20">
                                                                    <td colSpan={2} className="px-6 py-4">
                                                                        <div className="border border-[var(--color-border)] rounded-xl overflow-hidden overflow-x-auto bg-[var(--color-bg-card)]">
                                                                            <table className="w-full text-left border-collapse text-[11px]">
                                                                                <thead>
                                                                                    <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-[var(--color-border)] text-[var(--color-text-secondary)] uppercase tracking-widest font-black text-[9px]">
                                                                                        <th className="px-3 py-2">Invoice No</th>
                                                                                        <th className="px-3 py-2">Date</th>
                                                                                        <th className="px-3 py-2">Salesperson</th>
                                                                                        <th className="px-3 py-2 text-right">Sale Amount</th>
                                                                                        <th className="px-3 py-2 text-right">Pending Amount</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text-primary)] font-medium">
                                                                                    {(analytics.customerPendingDetails[item.name] || []).map((rec, rIdx) => (
                                                                                        <tr key={rIdx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-colors">
                                                                                            <td className="px-3 py-2 font-bold">{rec.invoiceNo}</td>
                                                                                            <td className="px-3 py-2 text-slate-500 font-bold">{formatDateString(rec.date)}</td>
                                                                                            <td className="px-3 py-2 font-bold">{rec.salesPerson}</td>
                                                                                            <td className="px-3 py-2 text-right font-mono">AED {rec.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                                            <td className="px-3 py-2 text-right font-black text-rose-500 font-mono">AED {rec.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Customer Pending Invoices Detail Modal */}
            {selectedPendingCustomer && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
                    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden">
                        {/* Modal Header */}
                        <div className="p-5 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-base font-black text-[var(--color-text-primary)]">
                                    Pending Invoices Details
                                </h3>
                                <p className="text-xs text-[var(--color-text-muted)] mt-1 font-bold">
                                    Customer: <span className="text-[var(--color-text-primary)]">{selectedPendingCustomer.name}</span>
                                </p>
                            </div>
                            <button 
                                onClick={() => setSelectedPendingCustomer(null)}
                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Summary Block */}
                        <div className="px-5 py-3.5 bg-rose-500/5 border-b border-[var(--color-border)] flex items-center justify-between text-xs shrink-0">
                            <span className="font-bold text-[var(--color-text-secondary)]">Total Outstanding:</span>
                            <span className="font-black text-rose-500 font-mono text-sm">
                                AED {selectedPendingCustomer.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>

                        {/* Scrollable Table */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-5">
                            {selectedPendingCustomer.details.length === 0 ? (
                                <div className="h-32 flex items-center justify-center text-gray-400 text-xs">No pending invoices found</div>
                            ) : (
                                <div className="border border-[var(--color-border)] rounded-xl overflow-hidden overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-900 border-b border-[var(--color-border)] text-[var(--color-text-secondary)] uppercase tracking-widest font-black text-[9px]">
                                                <th className="px-4 py-3">Invoice No</th>
                                                <th className="px-4 py-3">Date</th>
                                                <th className="px-4 py-3">Salesperson</th>
                                                <th className="px-4 py-3 text-right">Sale Amount</th>
                                                <th className="px-4 py-3 text-right">Pending Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text-primary)] font-medium">
                                            {selectedPendingCustomer.details.map((rec, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                    <td className="px-4 py-3.5 font-bold">{rec.invoiceNo}</td>
                                                    <td className="px-4 py-3.5 text-slate-500 font-bold">{formatDateString(rec.date)}</td>
                                                    <td className="px-4 py-3.5 font-bold">{rec.salesPerson}</td>
                                                    <td className="px-4 py-3.5 text-right font-mono">AED {rec.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td className="px-4 py-3.5 text-right font-black text-rose-500 font-mono">AED {rec.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-[var(--color-border)] flex justify-end shrink-0 bg-[var(--color-bg-hover)]">
                            <button
                                onClick={() => setSelectedPendingCustomer(null)}
                                className="px-4 py-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[var(--color-bg-hover)] transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Receive Payment Modal */}
            {showBulkReceiveModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
                    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                        {/* Header */}
                        <div className="p-5 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
                            <h3 className="text-base font-black text-[var(--color-text-primary)] flex items-center gap-2">
                                <CreditCard className="text-blue-500" />
                                Bulk Receive Payment
                            </h3>
                            <button
                                onClick={() => setShowBulkReceiveModal(false)}
                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-gray-400 hover:text-[var(--color-text-primary)] transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleBulkPaymentSubmit} className="p-5 space-y-4">
                            {modalError && (
                                <div className="p-3 bg-red-50 dark:bg-rose-950/20 border border-red-200 dark:border-rose-900/50 rounded-xl text-rose-600 dark:text-rose-400 text-xs font-bold">
                                    {modalError}
                                </div>
                            )}

                            {modalSuccess && (
                                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-xl text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                                    {modalSuccess}
                                </div>
                            )}

                            {/* Customer Select */}
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black uppercase tracking-widest text-[var(--color-text-secondary)]">
                                    Select Customer
                                </label>
                                <select
                                    required
                                    value={selectedCustomerId}
                                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                                    className="w-full bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 text-[var(--color-text-primary)]"
                                >
                                    <option value="">Choose a customer...</option>
                                    {customerList
                                        .map((cust) => {
                                            // Find pending amount if any
                                            const match = analytics.pendingPaymentsList.find(p => 
                                                p.name.trim().toUpperCase() === cust.name.trim().toUpperCase()
                                            );
                                            const bal = match ? match.amount : 0;
                                            return { cust, bal };
                                        })
                                        .filter(({ bal }) => bal > 0)
                                        .map(({ cust, bal }) => {
                                            return (
                                                <option key={cust.id} value={cust.id}>
                                                    {cust.name} (Outstanding: AED {bal.toFixed(2)})
                                                </option>
                                            );
                                        })
                                    }
                                </select>
                            </div>

                            {/* Outstanding Balance Display */}
                            {selectedCustomerId && (
                                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 border border-[var(--color-border)] rounded-xl flex items-center justify-between text-xs">
                                    <span className="font-bold text-[var(--color-text-secondary)]">Total Outstanding Balance</span>
                                    <span className="font-black text-rose-500">AED {selectedCustomerOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}

                            {/* Amount */}
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black uppercase tracking-widest text-[var(--color-text-secondary)]">
                                    Payment Amount (AED)
                                </label>
                                <input
                                    required
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedCustomerOutstanding}
                                    placeholder="0.00"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="w-full bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 text-[var(--color-text-primary)]"
                                />
                            </div>

                            {/* Payment Date */}
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black uppercase tracking-widest text-[var(--color-text-secondary)]">
                                    Payment Date
                                </label>
                                <CustomDatePicker
                                    mode="single"
                                    value={paymentDate}
                                    onChange={(val) => setPaymentDate(val)}
                                    quickFilters={false}
                                    className="w-full"
                                />
                            </div>

                            {/* Payment Mode */}
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black uppercase tracking-widest text-[var(--color-text-secondary)]">
                                    Payment Mode
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {['CASH', 'BANK'].map((mode) => (
                                        <button
                                            key={mode}
                                            type="button"
                                            onClick={() => setPaymentMode(mode)}
                                            className={`py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all border ${
                                                paymentMode === mode
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/10'
                                                    : 'bg-[var(--color-bg-card)] border-[var(--color-border)] text-gray-500 hover:bg-[var(--color-bg-hover)]'
                                            }`}
                                        >
                                            {mode}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Reference Number */}
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black uppercase tracking-widest text-[var(--color-text-secondary)]">
                                    Reference / Transaction No. (Optional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cheque no. / Bank transfer ref"
                                    value={referenceNo}
                                    onChange={(e) => setReferenceNo(e.target.value)}
                                    className="w-full bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 text-[var(--color-text-primary)]"
                                />
                            </div>

                            {/* Actions */}
                            <div className="pt-4 border-t border-[var(--color-border)] flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowBulkReceiveModal(false)}
                                    className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[var(--color-bg-hover)] transition-all text-[var(--color-text-primary)]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={modalLoading || !selectedCustomerId || !paymentAmount}
                                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-blue-600/50 disabled:to-indigo-600/50 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md shadow-blue-500/10 flex items-center gap-1.5"
                                >
                                    {modalLoading ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CreditCard size={14} />
                                            <span>Submit Payment</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <MergeCustomerModal
                isOpen={showMergeModal}
                initialTarget={selectedMergeCustomer}
                onClose={() => setShowMergeModal(false)}
                onSuccess={() => {
                    fetchData();
                }}
            />
        </div>
    );
};

export default OperationsAnalyticsView;
